/**
 * Hook повертає Set<string> модулів, які активовані хоча б у одній юрособі
 * поточного tenant'а (Порція 3.0.5.1c + fail-safe 2026-05-22).
 *
 * Логіка (за рішенням власника проекту 2026-05-15):
 *  - Беремо всі організації поточного tenant'а через GET /organizations.
 *  - Для кожної з business_template_id != null — резолвимо її template
 *    через GET /business-templates.
 *  - Об'єднуємо `enabled_modules` усіх знайдених templates.
 *
 * FAIL-SAFE FALLBACK (2026-05-22, UX permissions audit α follow-up):
 *  - Якщо tenant порожній (0 orgs) → ALL_AVAILABLE_MODULES.
 *  - Якщо є orgs, але ЖОДНА не має business_template_id → ALL_AVAILABLE_MODULES.
 *  - Якщо є orgs з template, але всі enabled_modules порожні → ALL_AVAILABLE_MODULES.
 *
 * Це системне рішення: імпорт з 1С, міграції, ручні INSERT'и, future
 * скрипти масового створення orgs — усі автоматично працюють без
 * пам'ятання про template. Раніше fallback працював лише для empty
 * tenant — ловило signup-flow, але не міграційні сценарії.
 *
 * Знайдено фактом 2026-05-22 через UX audit (PermissionTesting tenant
 * з 53 orgs без template — sidebar показав лише пункти без `module:`
 * атрибуту).
 *
 * Чому union а не "active org": для кожного документа фронт уже питає
 * organization_id окремо. Меню — глобальний шлях; user має бачити всі
 * шляхи, доступні хоча б у одній з його юросіб.
 */
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { businessTemplates } from '@/api/admin';
import { getMe } from '@/api/auth';
import { everVatPayer } from '@/shared/utils/vatFlags';

/**
 * Повний набір модулів, що використовуються у sidebar (layouts/sidebarItems.ts).
 * Збігається з union усіх enabled_modules системних business_templates,
 * але джерело істини — sidebar (бо саме там перевіряємо `hasModule`).
 *
 * Якщо у sidebar додається новий module-код — додати сюди.
 * (Sentinel-тест test_all_sidebar_modules_in_fallback ловить розрив.)
 */
export const ALL_AVAILABLE_MODULES = new Set<string>([
  'refs',
  'sales',
  'purchases',
  'cash',
  'bank',
  'warehouse',
  'tax',
  'accounting',
  'reports',
  'assets',
  'payroll',
  // ч.1082: 'retail' і 'vat' — за реальними org-флагами (use_retail /
  // is_vat_payer), як тара; див. flagModules нижче.
  'settlements', // Договори/Розрахунки (Фаза 2.1 #4, 2026-05-24)
  // NB: 'returnable_packaging' СВIДОМО НЕ тут — це особливий модуль,
  // прив'язаний до РЕАЛЬНОГО org-флага use_returnable_packaging, а не до
  // шаблону/набору «всіх доступних». Показуємо лише якщо хоча б в одній
  // юрособі увімкнено облік тари (див. _tareEnabledInAnyOrg нижче).
  // NB: 'production' (2026-08-17, рішення власника) — той самий тарний
  // паттерн: за реальним org-флагом use_recipes (галочка «Облік
  // виробництва» на картці орг) або production_costing_mode='1C_WIP'.
  // Меню стабільне у межах tenant'а (union по всіх орг — не «стрибає»
  // при перемиканні орг у шапці); create-endpoints виробництва мають
  // 422-gating require_org_flag_body('use_recipes').
]);

export interface EnabledModulesState {
  /** Set активованих модулів. */
  modules: Set<string>;
  /** True, поки запити йдуть. */
  isLoading: boolean;
  /** True, якщо у tenant'і ВЗАГАЛІ немає жодної організації. */
  isEmptyTenant: boolean;
  /**
   * True, якщо застосовано fail-safe fallback (modules == ALL_AVAILABLE_MODULES).
   * Optional для backward compat існуючих mock'ів у тестах (default false).
   */
  isFallback?: boolean;
}

export function useEnabledModules(): EnabledModulesState {
  // OWNER/ADMIN bypass (Порція Виробництво.5, 2026-05-22).
  // Tenant-адміни мають бачити ВСІ модулі незалежно від template'а:
  //  - вони керують шаблонами (tenant.template.manage) і повинні
  //    бачити повну картину функціоналу для свого вибору;
  //  - їх перевірка дублює fail-safe fallback, але виконується ДО
  //    fetch'у orgs/templates — стабільніше (нема race з useQuery).
  //
  // MEMBER'ам filter залишається активним: бачать лише модулі своїх
  // template'ів (UX rule «оператор не бачить непотрібного»).
  const meQuery = useQuery({ queryKey: ['me'], queryFn: getMe });
  const isOwnerOrAdmin =
    meQuery.data?.tenant_role === 'OWNER' ||
    meQuery.data?.tenant_role === 'ADMIN';

  const orgsQuery = useQuery({
    queryKey: ['orgs-for-modules'],
    queryFn: () => orgs.list({ limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });

  const templatesQuery = useQuery({
    queryKey: ['business-templates'],
    queryFn: businessTemplates.list,
    staleTime: 60 * 60 * 1000, // 1 година — templates рідко міняються
  });

  const isLoading =
    meQuery.isLoading || orgsQuery.isLoading || templatesQuery.isLoading;

  // Зворотна тара показується ЛИШЕ якщо хоча б в одній юрособі реально
  // увімкнено облік тари (флаг use_returnable_packaging). Це особливий модуль
  // поверх звичайної template-логіки — не у ALL_AVAILABLE_MODULES.
  const tareEnabled = (orgsQuery.data?.items ?? []).some(
    (o) => o.use_returnable_packaging,
  );
  const productionEnabled = (orgsQuery.data?.items ?? []).some(
    (o) => o.use_recipes || o.production_costing_mode === '1C_WIP',
  );
  // ч.1082 (рішення власника): «Роздріб» — лише якщо хоч в одній юрособі
  // стоїть галочка роздрібної торгівлі (облікова політика); «ПДВ» — лише
  // якщо є юрособа-платник ПДВ.
  const retailEnabled = (orgsQuery.data?.items ?? []).some((o) => o.use_retail);
  // ч.1383: і колишній платник — декларації й ПН за минулі періоди ще потрібні.
  const vatEnabled = (orgsQuery.data?.items ?? []).some((o) => everVatPayer(o));
  // ч.1376 (рішення власника): «Журнал доходів і витрат» — лише якщо хоч в одній юрособі
  // (ФОП-єдинник) стоїть галочка «Облік лише за Журналом доходів і витрат».
  const incomeJournalEnabled = (orgsQuery.data?.items ?? []).some((o) => o.income_journal_only);
  const withTare = (s: Set<string>): Set<string> => {
    s.delete('retail');
    if (tareEnabled) s.add('returnable_packaging');
    if (productionEnabled) s.add('production');
    if (retailEnabled) s.add('retail');
    if (vatEnabled) s.add('vat');
    if (incomeJournalEnabled) s.add('income_journal');
    return s;
  };

  // OWNER/ADMIN — повний набір без потреби чекати orgs/templates.
  // isLoading також false як тільки знаємо роль — щоб sidebar показав
  // меню одразу (UX швидкого старту для адмінів).
  if (isOwnerOrAdmin) {
    return {
      modules: withTare(new Set(ALL_AVAILABLE_MODULES)),
      isLoading: false,
      isEmptyTenant: false,
      isFallback: false,
    };
  }

  const orgsList = orgsQuery.data?.items ?? [];
  const isEmptyTenant = !isLoading && orgsList.length === 0;

  // Збираємо унікальні business_template_id з org.
  const templateIds = new Set(
    orgsList
      .map((o) => o.business_template_id)
      .filter((id): id is string => id !== null),
  );

  // Резолвимо їх через templates query.
  const templates = templatesQuery.data ?? [];
  const collected = new Set<string>();
  for (const tplId of templateIds) {
    const tpl = templates.find((t) => t.id === tplId);
    if (tpl) {
      // 'production' з шаблону ІГНОРУЄМО (2026-08-17): єдине джерело —
      // org-флаг use_recipes/1C_WIP (backfill 0430 вже увімкнув флаг
      // оргам з виробничим шаблоном — розсинхрону немає).
      for (const m of tpl.enabled_modules) {
        if (m !== 'production' && m !== 'retail') collected.add(m);
      }
    }
  }

  // FAIL-SAFE (2026-05-22): якщо isLoading — порожній Set (hasModule
  // дасть true через окремий isLoading-guard). Якщо завантажено й Set
  // empty — fallback на ALL_AVAILABLE_MODULES. Це покриває:
  //  - empty tenant (0 orgs)
  //  - orgs без business_template_id (імпорт/міграція)
  //  - orgs з template де enabled_modules=[] (corrupt template)
  const isFallback = !isLoading && collected.size === 0;
  const modules = withTare(isFallback ? new Set(ALL_AVAILABLE_MODULES) : collected);

  return { modules, isLoading, isEmptyTenant, isFallback };
}

/**
 * Перевірити чи модуль активований у поточному tenant'і.
 *
 * Fallback-логіка для UX (defense-in-depth):
 *  - isLoading — true (no flicker on init).
 *  - isEmptyTenant — true (backward compat для існуючих mock'ів +
 *    подвійний захист поверх hook-level fallback'у).
 *  - У всіх інших випадках читаємо з modules Set'у. Якщо у hook'у був
 *    застосований fail-safe (isFallback=true) — modules містить
 *    ALL_AVAILABLE_MODULES, тому повертатиме true.
 */
export function hasModule(
  state: EnabledModulesState,
  moduleCode: string,
): boolean {
  // Зворотна тара — суворо за реальним флагом org: НЕ показуємо у loading/
  // empty-fallback (інакше блимне при старті/порожньому тенанті). Лише коли
  // modules містить її (тобто флаг підтверджено хоча б в одній юрособі).
  if (['returnable_packaging', 'production', 'retail', 'vat', 'income_journal'].includes(moduleCode)) {
    return state.modules.has(moduleCode);
  }
  if (state.isLoading || state.isEmptyTenant) return true;
  return state.modules.has(moduleCode);
}
